import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listEquipment, getEquipmentUsageCounts } from '../features/equipment/api';
import type { EquipmentRow } from '../lib/types';
import { EQUIPMENT_CATEGORIES, label } from '../lib/vocab';
import { mediaUrl } from '../lib/storage';
import { Button } from '../components/Button';
import { Chip, ChipRow } from '../components/Chip';
import { Segmented } from '../components/Segmented';
import { Spinner } from '../components/Spinner';
import { searchRank, type Searchable } from '../lib/search';

type TriState = 'any' | 'with' | 'without';
const TRI_OPTIONS: ReadonlyArray<{ value: TriState; label: string }> = [
  { value: 'any', label: 'Any' },
  { value: 'with', label: 'With' },
  { value: 'without', label: 'Without' },
];

export function EquipmentListPage() {
  const [items, setItems] = useState<EquipmentRow[] | null>(null);
  const [usage, setUsage] = useState<Map<string, number>>(new Map());
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get('q') ?? '';
  const category = searchParams.get('category') || null;
  const photo = (searchParams.get('photo') as TriState | null) ?? 'any';
  const unused = searchParams.get('unused') === '1';
  const showArchived = searchParams.get('archived') === '1';

  useEffect(() => {
    listEquipment().then(setItems).catch(() => setItems([]));
    getEquipmentUsageCounts().then(setUsage).catch(() => setUsage(new Map()));
  }, []);

  function updateParams(mutate: (p: URLSearchParams) => void) {
    setSearchParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        mutate(out);
        return out;
      },
      { replace: true },
    );
  }
  const setQuery = (v: string) => updateParams((p) => (v ? p.set('q', v) : p.delete('q')));
  const setCategory = (v: string | null) => updateParams((p) => (v ? p.set('category', v) : p.delete('category')));
  const setPhoto = (v: TriState) => updateParams((p) => (v !== 'any' ? p.set('photo', v) : p.delete('photo')));
  const setUnused = (v: boolean) => updateParams((p) => (v ? p.set('unused', '1') : p.delete('unused')));
  const setShowArchived = (v: boolean) => updateParams((p) => (v ? p.set('archived', '1') : p.delete('archived')));

  function clearAll() {
    updateParams((p) => {
      ['q', 'category', 'photo', 'unused', 'archived'].forEach((k) => p.delete(k));
    });
  }

  function matchesFacets(e: EquipmentRow, excludeCategory = false, excludePhoto = false, excludeUnused = false, excludeArchived = false) {
    if (!excludeArchived && !showArchived && e.archived_at) return false;
    if (!excludeCategory && category && e.category !== category) return false;
    const hasPhoto = Boolean(e.photo_path);
    if (!excludePhoto && photo === 'with' && !hasPhoto) return false;
    if (!excludePhoto && photo === 'without' && hasPhoto) return false;
    const used = (usage.get(e.id) ?? 0) > 0;
    if (!excludeUnused && unused && used) return false;
    return true;
  }

  const searchMatchedIds = useMemo(() => {
    if (!items || !q.trim()) return null;
    const searchable: Searchable<string>[] = items.map((e) => ({ item: e.id, names: [e.name_en, e.name_he], extra: [label(e.category), label(e.class), e.id] }));
    return new Set(searchRank(q, searchable).map((x) => x.item));
  }, [items, q]);
  const passesSearch = (id: string) => searchMatchedIds === null || searchMatchedIds.has(id);

  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>();
    if (!items) return counts;
    for (const e of items) {
      if (!matchesFacets(e, true) || !passesSearch(e.id)) continue;
      counts.set(e.category, (counts.get(e.category) ?? 0) + 1);
    }
    return counts;
  }, [items, showArchived, photo, unused, usage, q, searchMatchedIds]);

  const photoCounts = useMemo(() => {
    let withCount = 0;
    let withoutCount = 0;
    if (!items) return { withCount, withoutCount };
    for (const e of items) {
      if (!matchesFacets(e, false, true) || !passesSearch(e.id)) continue;
      if (e.photo_path) withCount++;
      else withoutCount++;
    }
    return { withCount, withoutCount };
  }, [items, category, showArchived, unused, usage, q, searchMatchedIds]);

  const unusedCount = useMemo(() => {
    if (!items) return 0;
    let n = 0;
    for (const e of items) {
      if (!matchesFacets(e, false, false, true) || !passesSearch(e.id)) continue;
      if ((usage.get(e.id) ?? 0) === 0) n++;
    }
    return n;
  }, [items, category, showArchived, photo, usage, q, searchMatchedIds]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const facetFiltered = items.filter((e) => matchesFacets(e));
    if (!q.trim()) return [...facetFiltered].sort((a, b) => a.sort - b.sort);
    const searchable: Searchable<EquipmentRow>[] = facetFiltered.map((e) => ({
      item: e,
      names: [e.name_en, e.name_he],
      extra: [label(e.category), label(e.class), e.id],
    }));
    return searchRank(q, searchable).map((x) => x.item);
  }, [items, category, photo, unused, showArchived, usage, q]);

  type ActiveChip = { key: string; text: string; onRemove: () => void };
  const activeChips: ActiveChip[] = [];
  if (category) activeChips.push({ key: 'category', text: `Category: ${label(category)}`, onRemove: () => setCategory(null) });
  if (photo !== 'any') activeChips.push({ key: 'photo', text: `Photo: ${photo}`, onRemove: () => setPhoto('any') });
  if (unused) activeChips.push({ key: 'unused', text: 'Unused only', onRemove: () => setUnused(false) });
  if (showArchived) activeChips.push({ key: 'archived', text: 'Including archived', onRemove: () => setShowArchived(false) });
  if (q.trim()) activeChips.push({ key: 'q', text: `Search: "${q.trim()}"`, onRemove: () => setQuery('') });
  const hasAnyFilter = activeChips.length > 0;

  return (
    <div>
      <div className="topbar">
        <h1 className="page-title">Equipment</h1>
        <Link to="/equipment/new">
          <Button>Add equipment</Button>
        </Link>
      </div>

      <div className="filter-panel">
        <input
          className="input"
          placeholder="Search name (en/he)…"
          value={q}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: '100%' }}
        />

        <div className="filter-group">
          <span className="filter-group-label">Category</span>
          <ChipRow>
            <Chip active={category === null} onClick={() => setCategory(null)}>
              All
            </Chip>
            {EQUIPMENT_CATEGORIES.map((c) => (
              <Chip key={c} active={category === c} onClick={() => setCategory(category === c ? null : c)}>
                {label(c)} ({categoryCounts.get(c) ?? 0})
              </Chip>
            ))}
          </ChipRow>
        </div>

        <div className="filter-row">
          <Segmented
            label="Photo"
            value={photo}
            onChange={setPhoto}
            options={TRI_OPTIONS.map((o) => ({
              ...o,
              label: o.value === 'any' ? o.label : `${o.label} (${o.value === 'with' ? photoCounts.withCount : photoCounts.withoutCount})`,
            }))}
          />
          <div className="filter-group">
            <span className="filter-group-label">Usage</span>
            <ChipRow>
              <Chip active={unused} onClick={() => setUnused(!unused)}>
                Unused only ({unusedCount})
              </Chip>
            </ChipRow>
          </div>
          <div className="filter-group">
            <span className="filter-group-label">Archived</span>
            <ChipRow>
              <Chip active={showArchived} onClick={() => setShowArchived(!showArchived)}>
                {showArchived ? 'Showing archived' : 'Show archived'}
              </Chip>
            </ChipRow>
          </div>
        </div>
      </div>

      <div className="results-bar">
        <span className="results-count">{items === null ? '…' : `${filtered.length} of ${items.length} equipment`}</span>
        {hasAnyFilter ? (
          <Button tone="neutral" size="sm" onClick={clearAll}>
            Clear all
          </Button>
        ) : null}
      </div>

      {activeChips.length > 0 ? (
        <div className="active-filters" style={{ marginBottom: 16 }}>
          {activeChips.map((c) => (
            <span key={c.key} className="active-chip">
              {c.text}
              <button type="button" onClick={c.onRemove} aria-label={`Remove ${c.text}`}>
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {items === null ? (
        <Spinner />
      ) : (
        <div className="media-grid">
          {filtered.map((item) => (
            <Link key={item.id} to={`/equipment/${item.id}`} className="card equipment-card" style={{ textDecoration: 'none' }}>
              {item.photo_path ? (
                <img src={mediaUrl(item.photo_path) ?? undefined} alt={item.name_en} />
              ) : (
                <div className="thumb-placeholder" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span className="muted">{label(item.class)}</span>
                </div>
              )}
              <strong style={{ fontSize: 15 }}>{item.name_en}</strong>
              <span className="muted">
                {label(item.category)} · {label(item.class)}
                {item.archived_at ? ' · archived' : ''}
              </span>
              <span className={`badge ${(usage.get(item.id) ?? 0) > 0 ? '' : 'warn'}`}>
                used by {usage.get(item.id) ?? 0} {(usage.get(item.id) ?? 0) === 1 ? 'exercise' : 'exercises'}
              </span>
            </Link>
          ))}
          {filtered.length === 0 ? <p className="muted">No equipment matches.</p> : null}
        </div>
      )}
    </div>
  );
}
